import { useState } from 'react';
import { useFactoryRepoPolicies, useUpsertFactoryRepoPolicy } from '../../hooks/useFactoryRepoPolicies';
import { useFlag } from '../../services/flagsContext';
import type {
  FactoryRepoPolicyInput,
  FactoryRepoPolicyItem,
} from '../../services/api/factoryRepoPolicies';
import { Button } from '../common/Button';
import { RepoPolicyReadOnly } from './RepoPolicyReadOnly';

/**
 * MKS-471 (Phase 128 / Plan 128-03, D-21) — the per-repository Factory policy card: a global card on
 * the Factory tab of /admin/bot, next to the Robot token card.
 *
 * Phase 130 (Plan 130-11, D-01, D-17; Plan 130-15): the rules are read from `.monkfactory/factory.yml`
 * on main, so the card shows the resolved file read-only per repository (`RepoPolicyReadOnly`) and
 * edits only the Paperclip project name, a write of exactly `{ repository, paperclipProjectName }`.
 * The old gate/deploy editor and its reset are gone (D-01).
 *
 * The `mks_522_monkfactory_runtimes` flag stays registered until the automated release-flag cleanup
 * lands; both of its branches render the same read-only view, so that cleanup is a pure deletion of
 * the ternary.
 *
 * Presentation only; the server enforces the strict body.
 */

const INPUT_CLASSES =
  'block w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

/** The upsert body of a Paperclip-project-name save: the repository and the name, nothing else. */
function nameOnlyInput(item: FactoryRepoPolicyItem, name: string): FactoryRepoPolicyInput {
  const trimmed = name.trim();
  return {
    repository: item.repository,
    paperclipProjectName: trimmed.length > 0 ? trimmed : null,
  };
}

function PaperclipProjectNameEditor({
  item,
  save,
  pending,
  error,
}: {
  item: FactoryRepoPolicyItem;
  save: (input: FactoryRepoPolicyInput) => void;
  pending: boolean;
  error: string | null;
}) {
  const stored = item.paperclipProjectName ?? '';
  const [name, setName] = useState(stored);
  const inputId = `paperclip-project-name-${item.repository}`;
  return (
    <div className="mt-2" data-testid={`paperclip-name-${item.repository}`}>
      <label htmlFor={inputId} className="block text-xs font-medium text-gray-700">
        Paperclip project name
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          id={inputId}
          type="text"
          className={INPUT_CLASSES}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          type="button"
          size="sm"
          aria-label={`Save Paperclip project name of ${item.repository}`}
          disabled={pending || name.trim() === stored}
          onClick={() => save(nameOnlyInput(item, name))}
        >
          Save
        </Button>
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function FactoryRepoPolicyCard() {
  const { data, isLoading, isError, error } = useFactoryRepoPolicies();
  const upsert = useUpsertFactoryRepoPolicy();
  const runtimesOn = useFlag('mks_522_monkfactory_runtimes');
  // Which repository's name save is in flight or was refused, so a refusal never shows under another.
  const [savingRepo, setSavingRepo] = useState<string | null>(null);

  const policyList = (items: FactoryRepoPolicyItem[]) => (
    <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
      {items.map((item) => (
        <RepoPolicyReadOnly
          key={item.repository}
          item={item}
          note={
            <PaperclipProjectNameEditor
              item={item}
              save={(input) => {
                setSavingRepo(item.repository);
                upsert.mutate(input);
              }}
              pending={upsert.isPending && savingRepo === item.repository}
              error={
                upsert.isError && savingRepo === item.repository
                  ? (upsert.error as Error).message
                  : null
              }
            />
          }
        />
      ))}
    </ul>
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-sm font-semibold text-gray-900">Repository policies</p>
      <p className="mt-1 text-xs text-gray-500">
        The merge gate and the deploy check read .monkfactory/factory.yml on main for each
        repository on a project&apos;s repository list. A repository without a valid file uses the
        safe default: no required CI contexts, so the gate holds its pull requests for a human.
        Rules change by a reviewed pull request; only the Paperclip project name is set here.
      </p>

      {isLoading && <p className="mt-3 text-sm text-gray-500">Loading repository policies…</p>}
      {isError && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {data && (runtimesOn ? policyList(data) : policyList(data))}
    </div>
  );
}
